import type { ProductCostState } from "@/hooks/useComparisonCosts";
import { parseDecimalString } from "@/utils/cost-display";

/**
 * POC parity: the global useful-life
 * "Totals (N years)" rollup shown below the comparison action bar. It combines
 * the one-time configured machine investment with useful-life labour and
 * quality costs across every selected application on its ASSIGNED machine.
 *
 * Per-app labour/quality annual cost is `*CostPerBatch × batchesPerYear`.
 * Lifetime = annual × usefulLifeYears. Machine investment is counted once per
 * assigned product, not once per application or once per useful-life year.
 */
export type GlobalUsefulLifeTotals = {
  usefulLifeYears: number;
  machineTotal: number;
  labourTotal: number;
  qualityTotal: number;
  grandTotal: number;
  labourPerYear: number;
  qualityPerYear: number;
  grandPerYear: number;
  currencyCode: string | null;
};

type AppRef = { applicationId: string };

/**
 * Returns null unless EVERY selected application is assigned to a machine whose
 * cost preview is `ok` and contains a row for that application — mirroring the
 * POC `ok` / `allAssigned` guard that keeps the panel hidden while any assigned
 * cost is still missing or loading.
 */
export function computeGlobalUsefulLifeTotals(params: {
  appRefs: AppRef[];
  selectedMachineByApp: Record<string, string>;
  costByProductId: Record<string, ProductCostState>;
  /** Quote-only rows added to the displayed one-time investment, by product. */
  manualLineTotalByProductId?: Readonly<Record<string, number>>;
  usefulLifeYears: number;
}): GlobalUsefulLifeTotals | null {
  const { appRefs, selectedMachineByApp, costByProductId, usefulLifeYears } =
    params;
  if (appRefs.length === 0) return null;

  const years =
    Number.isFinite(usefulLifeYears) && usefulLifeYears > 0
      ? usefulLifeYears
      : 7;

  let labourAnnual = 0;
  let qualityAnnual = 0;
  let currencyCode: string | null = null;
  const assignedByProduct = new Map<
    string,
    {
      cost: Extract<ProductCostState, { status: "ok" }>;
      assignedApplicationIds: string[];
    }
  >();

  for (const ref of appRefs) {
    const appId = ref.applicationId;
    const machineId = selectedMachineByApp[appId];
    if (!machineId) return null; // POC `allAssigned` — not every app assigned.

    const cost = costByProductId[machineId];
    if (!cost || cost.status !== "ok") return null; // assigned cost missing.

    const row = cost.data.perApp.find((r) => r.applicationId === appId);
    if (!row) return null;
    if (!currencyCode) currencyCode = cost.data.currencyCode ?? null;

    const batchesPerYear = parseDecimalString(row.batchesPerYear);
    if (batchesPerYear == null) return null;

    labourAnnual +=
      (parseDecimalString(row.workerCostPerBatch) ?? 0) * batchesPerYear;
    qualityAnnual +=
      (parseDecimalString(row.qualityCostPerBatch) ?? 0) * batchesPerYear;

    const existing = assignedByProduct.get(machineId) ?? {
      cost,
      assignedApplicationIds: [],
    };
    existing.assignedApplicationIds.push(appId);
    assignedByProduct.set(machineId, existing);
  }

  const labourTotal = labourAnnual * years;
  const qualityTotal = qualityAnnual * years;
  let machineTotal = 0;

  // The hook computes the same assignment-scoped selected catalog total shown
  // when Configure Solution opens, including family-specific quantity rules.
  // Count it once per product; never multiply every option by requiredMachines.
  for (const [
    productId,
    { cost, assignedApplicationIds },
  ] of assignedByProduct) {
    const currentAssignmentKey = [...assignedApplicationIds].sort().join(",");
    if (
      cost.assignmentKey !== undefined &&
      cost.assignmentKey !== currentAssignmentKey
    ) {
      // Assignment changed and the matching preview is still in flight.
      return null;
    }

    const recommendedTotal = parseDecimalString(
      cost.recommendedConfigurationTotal ?? null,
    );
    if (recommendedTotal == null) return null;
    machineTotal += recommendedTotal;

    // Manual quote rows do not affect Calculate or the utilization/KPI model.
    // They do belong in both the displayed and optimized one-time investment,
    // exactly once for each machine product used by the final assignment.
    const manualTotal = params.manualLineTotalByProductId?.[productId] ?? 0;
    if (Number.isFinite(manualTotal) && manualTotal > 0) {
      machineTotal += manualTotal;
    }
  }

  const grandTotal = machineTotal + labourTotal + qualityTotal;

  return {
    usefulLifeYears: years,
    machineTotal,
    labourTotal,
    qualityTotal,
    grandTotal,
    labourPerYear: labourAnnual,
    qualityPerYear: qualityAnnual,
    grandPerYear: grandTotal / years,
    currencyCode,
  };
}
